import React, { useCallback, useEffect, useRef, useState } from 'react'
import { BiMenu } from 'react-icons/bi'
import Header from './Header'
import SideNavAff from './SideNavAff'

/* ------------------------------------------------------------------ */
/* Shared layout for every affiliate page: header + side nav + content. */
/*                                                                      */
/* The content's left margin follows the real width of the side nav, so  */
/* nothing is hidden underneath it when it collapses or opens. If the    */
/* width can't be measured (the nav root isn't a fixed element), it      */
/* falls back to the two constants below. Adjust them to match           */
/* SideNavAff if you ever change its CSS.                                */
/* ------------------------------------------------------------------ */
const SIDEBAR_COLLAPSED = 80
const SIDEBAR_OPEN = 240
const HEADER_HEIGHT = 60
const MOBILE_BREAKPOINT = 768

const isMobileWidth = () => window.innerWidth <= MOBILE_BREAKPOINT

const AffiliateShell = ({ children, showNav = true }) => {
  const [isMobile, setIsMobile] = useState(isMobileWidth())
  const [collapsed, setCollapsed] = useState(isMobileWidth())
  const [navWidth, setNavWidth] = useState(null)
  const navRef = useRef(null)

  const toggle = useCallback(() => setCollapsed((c) => !c), [])

  // keep the layout in sync with the window; auto-collapse only when entering mobile
  useEffect(() => {
    const onResize = () => {
      const mobile = isMobileWidth()
      setIsMobile(mobile)
      if (mobile) setCollapsed(true)
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // follow the side nav's real width, including while it animates
  useEffect(() => {
    const root = navRef.current ? navRef.current.firstElementChild : null
    if (!showNav || !root || typeof ResizeObserver === 'undefined') {
      setNavWidth(null)
      return undefined
    }
    const read = () => {
      const position = window.getComputedStyle(root).position
      const width = Math.round(root.getBoundingClientRect().width)
      const usable = (position === 'fixed' || position === 'sticky') && width >= 40 && width <= 400
      setNavWidth(usable ? width : null)
    }
    read()
    const observer = new ResizeObserver(read)
    observer.observe(root)
    return () => observer.disconnect()
  }, [showNav, collapsed, isMobile])

  const marginLeft =
    !showNav || isMobile ? 0 : (navWidth ?? (collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_OPEN))

  return (
    <div className="view">
      <Header />

      {showNav && (
        // display: contents keeps this wrapper out of the layout, so the nav's own positioning is untouched
        <div ref={navRef} style={{ display: 'contents' }}>
          <SideNavAff collapsed={collapsed} onToggle={toggle} />
        </div>
      )}

      <div className="main-content" style={{ marginTop: HEADER_HEIGHT, marginLeft, minWidth: 0 }}>
        {showNav && isMobile && !collapsed && (
          <div className="mobile-overlay active" onClick={toggle} />
        )}

        {showNav && isMobile && collapsed && (
          <label className="mobile-toggle" onClick={toggle}>
            <BiMenu size={25} />
          </label>
        )}

        {children}
      </div>
    </div>
  )
}

export default AffiliateShell
